import React, { useState, useEffect } from 'react';
import { HiChevronDown, HiSearch, HiX } from 'react-icons/hi';
import classNames from 'classnames';
import { Category, CategoryType, getCategoryTypeLabel } from '@/types/category';
import { categoryApi } from '@/api/endpoints/categories';

interface CategorySelectorProps {
  value?: string;
  onChange: (categoryId: string) => void;
  placeholder?: string;
  disabled?: boolean;
  excludeId?: string; // Exclude this category to prevent circular reference
  typeFilter?: CategoryType[]; // Only show categories of these types
  className?: string;
}

const CategorySelector: React.FC<CategorySelectorProps> = ({
  value,
  onChange,
  placeholder = 'Chọn danh mục cha',
  disabled = false,
  excludeId,
  typeFilter,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    // Filter categories based on search query and type filter
    let filtered = categories;

    // Exclude the specified category to prevent circular reference
    if (excludeId) {
      filtered = filtered.filter(cat => cat.id !== excludeId);
    }

    // Filter by type if specified
    if (typeFilter && typeFilter.length > 0) {
      filtered = filtered.filter(cat => typeFilter.includes(cat.type));
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(cat => 
        cat.name.toLowerCase().includes(query) ||
        cat.code.toLowerCase().includes(query) ||
        (cat.description && cat.description.toLowerCase().includes(query))
      );
    }

    setFilteredCategories(filtered);
  }, [categories, searchQuery, excludeId, typeFilter]);

  useEffect(() => {
    // Find and set selected category when value changes
    if (value) {
      const category = categories.find(cat => cat.id === value);
      setSelectedCategory(category || null);
    } else {
      setSelectedCategory(null);
    }
  }, [value, categories]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await categoryApi.getCategories(1, 1000); // Get all categories
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (category: Category) => {
    onChange(category.id);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setIsOpen(false);
  };

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className={classNames('relative', className)}>
      {/* Selector Button */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={classNames(
          'w-full flex items-center justify-between px-3 py-2 border rounded-lg text-left transition-colors',
          {
            'border-gray-300 bg-white hover:border-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500': !disabled,
            'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed': disabled,
            'border-primary-500 ring-1 ring-primary-500': isOpen && !disabled,
          }
        )}
      >
        <div className="flex-1 min-w-0">
          {selectedCategory ? (
            <div className="flex items-center space-x-2">
              <span className="font-medium text-gray-900 truncate">
                {selectedCategory.name}
              </span>
              <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                {getCategoryTypeLabel(selectedCategory.type)}
              </span>
            </div>
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </div>
        
        <div className="flex items-center space-x-1 ml-2">
          {selectedCategory && !disabled && (
            <button
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <HiX className="w-4 h-4 text-gray-400" />
            </button>
          )}
          <HiChevronDown 
            className={classNames('w-4 h-4 text-gray-400 transition-transform', {
              'transform rotate-180': isOpen,
            })} 
          />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-hidden">
          {/* Search Input */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Tìm kiếm danh mục..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Category List */}
          <div className="max-h-48 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                Đang tải...
              </div>
            ) : filteredCategories.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                {searchQuery ? 'Không tìm thấy danh mục phù hợp' : 'Không có danh mục nào'}
              </div>
            ) : (
              filteredCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleSelect(category)}
                  className={classNames(
                    'w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0',
                    {
                      'bg-primary-50': category.id === value,
                    }
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-gray-900 truncate">
                          {category.name}
                        </span>
                        <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                          {getCategoryTypeLabel(category.type)}
                        </span>
                      </div>
                      {category.description && (
                        <div className="text-sm text-gray-500 truncate mt-1">
                          {category.description}
                        </div>
                      )}
                    </div>
                    {category.materialCount !== undefined && category.materialCount > 0 && (
                      <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                        {category.materialCount} NVL
                      </span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default CategorySelector;
